import { format, isMatch, isValid, parseISO, subDays } from "date-fns";
import { splitHash, type HashParamKey } from "../../utils/urlHash";

export type DateRangePreset = "all" | "custom" | `${number}d`;
export type DashboardDateSelection = {
  from: string;
  to: string;
  preset: DateRangePreset;
  customRange?: { from: string; to: string };
};

export const allTime: DashboardDateSelection = {
  from: "",
  to: "",
  preset: "all",
};
/** Short local dates ("Apr 10"), with years once any of `dates` falls outside this year. */
export const dashboardDateFormatter = (dates: string[], language?: string) => {
  const showYear = dates.some(
    date => date && date.slice(0, 4) !== String(new Date().getFullYear()),
  );
  const formatter = new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    ...(showYear ? { year: "numeric" } : {}),
  });
  return (date: string) => {
    const parsed = parseISO(date);
    return isValid(parsed) ? formatter.format(parsed) : date;
  };
};

export const validDate = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && isMatch(value, "yyyy-MM-dd");
export const rollingDays = (preset: string) => {
  const days = /^\d+d$/.test(preset) ? Number(preset.slice(0, -1)) : 0;
  return Number.isInteger(days) && days >= 1 && days <= 36500 ? days : null;
};
export const lastDays = (days: number, today = new Date()) => ({
  from: format(subDays(today, days - 1), "yyyy-MM-dd"),
  to: format(today, "yyyy-MM-dd"),
});
export const lastThirtyDays = () => lastDays(30);

/**
 * Request bounds for a picked range of the viewer's local days: from the first day's local
 * midnight to the last day's final millisecond. Every page asks the API with these, so a session
 * or a case falls on the same day wherever it is counted.
 */
export const localDayBounds = (from: string, to: string) => ({
  datetime_gte: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
  datetime_lte: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
});
export const resolveDates = (selection: DashboardDateSelection): DashboardDateSelection => {
  const days = rollingDays(selection.preset);
  return days ? { ...selection, ...lastDays(days) } : selection;
};

// The shared date params, plus the per-page pairs older links still carry.
export const DATE_PERIOD_HASH_KEY = "period";
export const DATE_FROM_HASH_KEY = "from";
export const DATE_TO_HASH_KEY = "to";
export const LEGACY_SESSION_DATE_FROM_HASH_KEY = "rdf";
export const LEGACY_SESSION_DATE_TO_HASH_KEY = "rdt";

const legacyPairs = [
  ["odf", "odt"],
  ["pdf", "pdt"],
  ["rdf", "rdt"],
] as const;
export function readDateSelection(
  hash: string,
  remembered: DashboardDateSelection | null = null,
): DashboardDateSelection {
  const { params } = splitHash(hash);
  const canonical = ["period", "from", "to"].some(key => params.has(key as HashParamKey));
  const pair = canonical
    ? (["from", "to"] as const)
    : legacyPairs.find(keys => keys.some(key => params.has(key)));
  const period = canonical ? params.get("period") : null;
  let next: DashboardDateSelection;
  if (period === "all") next = allTime;
  else if (period && rollingDays(period))
    next = {
      ...lastDays(rollingDays(period)!),
      preset: period as DateRangePreset,
    };
  else if (pair) {
    const from = params.get(pair[0]) ?? "";
    const to = params.get(pair[1]) ?? "";
    const valid =
      (!from || validDate(from)) && (!to || validDate(to)) && !(from && to && from > to);
    next = valid && (from || to) ? { from, to, preset: "custom" } : allTime;
  } else next = canonical ? allTime : resolveDates(remembered ?? allTime);
  return {
    ...next,
    customRange:
      next.preset === "custom" ? { from: next.from, to: next.to } : remembered?.customRange,
  };
}

export const dateSelectionHash = (
  selection: DashboardDateSelection,
): Partial<Record<HashParamKey, string | null>> => ({
  period: selection.preset === "custom" ? null : selection.preset,
  from: selection.preset === "custom" ? selection.from || null : null,
  to: selection.preset === "custom" ? selection.to || null : null,
  odf: null,
  odt: null,
  pdf: null,
  pdt: null,
  rdf: null,
  rdt: null,
});

export const dateStorageKey = (userId: string, companyId: number | null) =>
  `clank.filter.dates.v1:${companyId ?? "personal"}:${userId}`;

export function loadDateSelection(key: string): DashboardDateSelection {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null");
    if (!saved || typeof saved.preset !== "string") return allTime;
    const validRange = (range: { from?: unknown; to?: unknown } | null) =>
      range &&
      (range.from === "" || validDate(range.from)) &&
      (range.to === "" || validDate(range.to)) &&
      (range.from || range.to) &&
      !(range.from && range.to && range.from > range.to);
    const customRange = validRange(saved.customRange) ? saved.customRange : undefined;
    if (saved.preset === "all") return { ...allTime, customRange };
    const days = rollingDays(saved.preset);
    if (days) return { ...lastDays(days), preset: saved.preset, customRange };
    if (saved.preset === "custom" && validRange(saved))
      return {
        from: saved.from,
        to: saved.to,
        preset: "custom",
        customRange: { from: saved.from, to: saved.to },
      };
  } catch {
    /* Storage may be disabled or contain an older, invalid value. */
  }
  return allTime;
}

export function saveDateSelection(key: string, selection: DashboardDateSelection) {
  try {
    // Relative ranges retain their meaning when the app is reopened on another day.
    localStorage.setItem(
      key,
      JSON.stringify({
        preset: selection.preset,
        ...(selection.preset === "custom" && {
          from: selection.from,
          to: selection.to,
        }),
        customRange: selection.customRange,
      }),
    );
  } catch {
    /* Keep the in-memory selection when browser storage is unavailable. */
  }
}
